import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import { useRfqMutations } from '../hooks/useRfqMutations'
import { formatMoney } from '../lib/money'
import { awardResultPath, canAwardQuote } from '../lib/rfq'
import type { CostRfq, CostRfqDetail } from '../types'

interface Props {
  projectId: string
  currency: string
  locale: string
  onClose: () => void
}

/**
 * Freelo #33300943: "Nová změna" from an RFQ — picking the winning quote of an open RFQ on a contract is the RFQ award,
 * which drafts the contract change (part D spec §4); the change opens next.
 */
const ChangeFromRfqDialog: React.FC<Props> = ({ projectId, currency, locale, onClose }) => {
  const { t } = useTranslation()
  const { history } = useRouter()
  const mutations = useRfqMutations(projectId)
  const [rfqId, setRfqId] = useState<string>()
  const [quoteId, setQuoteId] = useState<string>()
  const [submitting, setSubmitting] = useState(false)
  const { data: rfqData, loading } = useQuery<{ costRfqs: CostRfq[] }>(Query.COST_RFQS, {
    variables: { projectId, filter: { status: 'OPEN' } },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const { data: detailData } = useQuery<{ costRfq: CostRfqDetail }>(Query.COST_RFQ, {
    variables: { id: rfqId },
    skip: !rfqId,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const rfqs = (rfqData?.costRfqs ?? []).filter((r) => r.contractId && r.quotedCount > 0)
  // The detail of the previous pick can still be in `detailData` until the new one loads.
  const quotes = detailData && detailData.costRfq.rfq.id === rfqId ? detailData.costRfq.quotes.filter((q) => canAwardQuote({ award: true }, q)) : []
  const quote = quotes.find((q) => q.id === quoteId)
  const money = (v: string) => formatMoney(v, { locale, currency })

  const submit = async () => {
    if (!rfqId || !quoteId) return
    setSubmitting(true)
    const res = await mutations.award(rfqId, quoteId)
    setSubmitting(false)
    const path = res.ok ? awardResultPath(projectId, res.data.awardCostRfq) : null
    if (path) history.push(path)
  }

  return (
    <Dialog open onOpenChange={(next) => { if (!next && !submitting) onClose() }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('CostControl_change_from_rfq_title')}</DialogTitle>
          <DialogDescription>{t('CostControl_change_from_rfq_hint')}</DialogDescription>
        </DialogHeader>
        {!rfqData && loading && <div className="flex justify-center py-6"><LoaderCircle className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {rfqData && rfqs.length === 0 && <p className="text-sm text-muted-foreground">{t('CostControl_change_from_rfq_empty')}</p>}
        {rfqs.length > 0 && (
          <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); submit() }}>
            <div className="space-y-1.5">
              <Label htmlFor="cc-from-rfq">{t('CostControl_change_from_rfq_rfq')}</Label>
              <Select value={rfqId ?? ''} onValueChange={(v) => { setRfqId(v); setQuoteId(undefined) }}>
                <SelectTrigger id="cc-from-rfq"><SelectValue placeholder={t('Common_select')} /></SelectTrigger>
                <SelectContent>
                  {rfqs.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {t('CostControl_rfq_number', { number: r.number })} · {r.title} · {r.contractNumber ? `${r.contractNumber} · ${r.contractName}` : r.contractName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cc-from-rfq-quote">{t('CostControl_change_from_rfq_quote')}</Label>
              <Select value={quoteId ?? ''} onValueChange={setQuoteId} disabled={!quotes.length}>
                <SelectTrigger id="cc-from-rfq-quote"><SelectValue placeholder={t('Common_select')} /></SelectTrigger>
                <SelectContent>
                  {quotes.map((q) => <SelectItem key={q.id} value={q.id}>{q.supplierName} · {money(q.amount!)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {quote && <p className="text-sm text-muted-foreground">{t('CostControl_rfq_award_text_change', { supplier: quote.supplierName, amount: money(quote.amount!) })}</p>}
            <div className="flex items-center justify-end gap-2 border-t pt-4">
              <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>{t('Common_cancel')}</Button>
              <Button type="submit" disabled={submitting || !quote}>
                {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
                {t('CostControl_change_from_rfq_submit')}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
export default ChangeFromRfqDialog
